export type RgbColor = { r: number; g: number; b: number };

const COOL_RGB: RgbColor = { r: 208, g: 229, b: 255 };
const WARM_RGB: RgbColor = { r: 255, g: 174, b: 82 };

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

/**
 * Hue plus HSV saturation (vividness). Matches {@link hueSaturationToRgb},
 * which mixes a full-chroma hue toward white. HSL saturation would read every
 * mixed color as 100%.
 */
export const rgbToHueSat = ({ r, g, b }: RgbColor): { h: number; s: number } => {
	const red = r / 255;
	const green = g / 255;
	const blue = b / 255;
	const max = Math.max(red, green, blue);
	const min = Math.min(red, green, blue);
	const delta = max - min;
	let h = 0;
	if (delta) {
		if (max === red) h = ((green - blue) / delta) % 6;
		else if (max === green) h = (blue - red) / delta + 2;
		else h = (red - green) / delta + 4;
		h = Math.round(h * 60);
		if (h < 0) h += 360;
	}
	const saturation = max ? delta / max : 0;
	return { h, s: Math.round(saturation * 100) };
};

export const hueSaturationToRgb = (h: number, s: number): RgbColor => {
	const saturationLevel = clamp(s, 0, 100) / 100;
	const chroma = 1;
	const hue = ((h % 360) + 360) % 360;
	const segment = hue / 60;
	const x = chroma * (1 - Math.abs((segment % 2) - 1));
	let channels = [0, 0, 0];
	if (segment < 1) channels = [chroma, x, 0];
	else if (segment < 2) channels = [x, chroma, 0];
	else if (segment < 3) channels = [0, chroma, x];
	else if (segment < 4) channels = [0, x, chroma];
	else if (segment < 5) channels = [x, 0, chroma];
	else channels = [chroma, 0, x];
	return {
		r: Math.round(255 + (channels[0] * 255 - 255) * saturationLevel),
		g: Math.round(255 + (channels[1] * 255 - 255) * saturationLevel),
		b: Math.round(255 + (channels[2] * 255 - 255) * saturationLevel),
	};
};

export const ctToPreviewRgb = (ct: number, minValue: number, maxValue: number): RgbColor => {
	const span = maxValue - minValue;
	const t = span === 0 ? 0 : clamp((maxValue - ct) / span, 0, 1);
	return {
		r: Math.round(COOL_RGB.r + (WARM_RGB.r - COOL_RGB.r) * t),
		g: Math.round(COOL_RGB.g + (WARM_RGB.g - COOL_RGB.g) * t),
		b: Math.round(COOL_RGB.b + (WARM_RGB.b - COOL_RGB.b) * t),
	};
};

export const rgbCss = ({ r, g, b }: RgbColor): string => `rgb(${r} ${g} ${b})`;

/** Warm preview used on brightness-only dimmer islands. */
export const DIMMER_PREVIEW_RGB: RgbColor = { r: 255, g: 196, b: 120 };

export const HUE_GRADIENT =
	'linear-gradient(90deg, #f00 0%, #ff0 16.667%, #0f0 33.333%, #0ff 50%, #00f 66.667%, #f0f 83.333%, #f00 100%)';

export const WARMTH_GRADIENT = 'linear-gradient(90deg, rgb(208 229 255), rgb(255 174 82))';
